// Outcome of a playRingtone() attempt. "blocked" is the browser autoplay
// policy rejection (NotAllowedError), expected on a fresh page load with no
// user activation; the caller decides how to recover (VideoConsultAlert
// shows an explicit retry control). "superseded" means the attempt was
// invalidated while pending (stopRingtone or a newer attempt) and its
// settlement was ignored; callers should treat it as "nothing to do".
// "failed" is any other error.
export type RingtonePlayOutcome =
  | "started"
  | "already-playing"
  | "blocked"
  | "failed"
  | "superseded";

type PlaybackState = "idle" | "pending" | "playing";

class AudioPlayer {
  private static instance: AudioPlayer;
  private ringtoneAudio: HTMLAudioElement | null = null;
  // Three-way state: a pending play() is NOT playing: a concurrent caller
  // holding a real user gesture must still issue its own play() call.
  private state: PlaybackState = "idle";
  // Attempt identity, bumped by every new attempt and by stopRingtone(): a
  // stale promise settlement (e.g. the AbortError a pause() forces out of a
  // pending play()) must never mutate the current attempt's state.
  private attemptId = 0;

  private constructor() {}

  public static getInstance(): AudioPlayer {
    if (!AudioPlayer.instance) {
      AudioPlayer.instance = new AudioPlayer();
    }
    return AudioPlayer.instance;
  }

  public async playRingtone(): Promise<RingtonePlayOutcome> {
    if (this.state === "playing") {
      return "already-playing";
    }

    // Create audio element if it doesn't exist
    if (!this.ringtoneAudio) {
      this.ringtoneAudio = new Audio("/assets/sounds/phone-ring.mp3");
      this.ringtoneAudio.loop = true;
      this.ringtoneAudio.volume = 0.7;
    }

    // Supersede any pending attempt instead of joining it: this call may
    // carry a user gesture, so it must reach play() itself.
    const attempt = ++this.attemptId;
    this.state = "pending";

    try {
      // No await may precede this call: when invoked from a click handler,
      // play() must run synchronously within the gesture (Safari requires it).
      await this.ringtoneAudio.play();
      if (attempt !== this.attemptId) {
        return "superseded";
      }
      this.state = "playing";
      return "started";
    } catch (error) {
      if (attempt !== this.attemptId) {
        return "superseded";
      }
      this.state = "idle";
      if ((error as DOMException)?.name === "NotAllowedError") {
        return "blocked";
      }
      console.error("Failed to play ringtone:", error);
      return "failed";
    }
  }

  public stopRingtone(): void {
    // Invalidate any pending attempt FIRST, so the rejection that pause()
    // forces out of a pending play() settles as stale and changes nothing.
    this.attemptId++;
    this.state = "idle";
    // Pause unconditionally, never trust bookkeeping to decide whether the
    // element is audible. Pausing a non-playing element is harmless.
    if (this.ringtoneAudio) {
      this.ringtoneAudio.pause();
      this.ringtoneAudio.currentTime = 0;
    }
  }

  public isRingtonePlaying(): boolean {
    return this.state === "playing";
  }
}

export default AudioPlayer;
